import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useCart } from "@/context/CartContext";
import { fileUrl } from "@/lib/api";
import { brandFromPath } from "@/lib/themes";

const BRANDS = [
  { key: "stamp", label: "K8 STAMP", badge: "Uniformes & Brindes", route: "/" },
  { key: "sports", label: "K8 SPORTS", badge: "Esportivo", route: "/k8-sports" },
];

export default function Header() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { settings } = useSite();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const brand = brandFromPath(pathname);

  const logo =
    brand === "sports" ? settings?.sports_logo_url : settings?.stamp_logo_url;
  const campaignOpen = settings?.campaign_open;

  const navLinks =
    brand === "sports"
      ? [
          { label: "Uniformes", href: "#uniformes" },
          { label: "Kits", href: "#kits" },
          { label: "Galeria", href: "#galeria" },
          { label: "Como pedir", href: "#como-pedir" },
          { label: "FAQ", href: "#faq" },
        ]
      : [
          { label: "Produtos", href: "#produtos" },
          { label: "Trabalhos", href: "#galeria" },
          { label: "Como pedir", href: "#como-pedir" },
          { label: "FAQ", href: "#faq" },
        ];

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-4">
          <Link
            to={brand === "sports" ? "/k8-sports" : "/"}
            data-testid="header-logo-link"
            className="flex items-center shrink-0"
          >
            {logo ? (
              <img
                src={fileUrl(logo)}
                alt={brand === "sports" ? "K8 Sports" : "K8 Stamp"}
                className="h-12 w-auto object-contain logo-outline"
              />
            ) : (
              <span className="font-head font-extrabold text-xl">
                {brand === "sports" ? "K8 SPORTS" : "K8 STAMP"}
              </span>
            )}
          </Link>

          {/* Brand switcher */}
          <div className="hidden md:flex items-center gap-1 rounded-full p-1 border border-[var(--border)] bg-[var(--bg-secondary)]">
            {BRANDS.map((b) => {
              const active = b.key === brand;
              return (
                <button
                  key={b.key}
                  data-testid={`nav-brand-${b.key}`}
                  onClick={() => navigate(b.route)}
                  className={`px-4 py-2 rounded-full text-xs font-bold tracking-wide transition-all ${
                    active
                      ? "text-[var(--btn-text)]"
                      : "text-[var(--text-muted)] hover:text-[var(--text-main)]"
                  }`}
                  style={active ? { background: "var(--accent)" } : {}}
                >
                  {b.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            {campaignOpen && (
              <Link
                to="/pagode-das-cinco-pontas"
                data-testid="nav-brand-pagode"
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold btn-primary"
              >
                <ShoppingBag size={15} /> Loja do Evento
              </Link>
            )}
            <button
              className="md:hidden p-2"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Desktop section nav */}
        <nav className="hidden md:flex items-center gap-8 h-11 -mt-1">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>
      </div>

      {open && (
        <div className="md:hidden border-t border-[var(--border)] bg-[var(--bg-secondary)] px-4 py-4 space-y-3">
          <div className="flex gap-2">
            {BRANDS.map((b) => (
              <button
                key={b.key}
                data-testid={`mobile-brand-${b.key}`}
                onClick={() => {
                  navigate(b.route);
                  setOpen(false);
                }}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold ${
                  b.key === brand
                    ? "btn-primary"
                    : "border border-[var(--border)] text-[var(--text-muted)]"
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block py-1 text-[var(--text-main)]"
            >
              {l.label}
            </a>
          ))}
          {campaignOpen && (
            <Link
              to="/pagode-das-cinco-pontas"
              onClick={() => setOpen(false)}
              className="block py-2 px-3 rounded-lg btn-primary text-center font-bold"
            >
              Loja do Evento
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
